import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useJobs } from "../../../hooks/useJobs";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import type { Job, NurseRole } from "../../../utils/models";
import { nurseRoleDisplayName } from "../../../utils/utilFunctions";
import { SystemDropdown } from "../Inputs/SystemDropDown";
import { JobSelector } from "./JobSelector";

type Props = {
	selectedRoleGroups: string[];
	onSelectedRoleGroupsChanged: (selected: string[]) => void;
	selectedJobIds: string[];
	onSelectedJobsChanged: (selected: string[]) => void;
	/**
	 * Pick exactly one job rather than a set — for a form that CREATES something (a post is for one job), where the
	 * filter's multi-pick would let a user build a request the backend cannot represent. Role groups stay multi.
	 */
	singleJob?: boolean;
	/** Stretch either picker to its container, as a form field does. The role-group picker already fills it. */
	fullWidth?: boolean;
	/**
	 * Drop the picker's own floating label ("Job" / "Role groups"), for a form that labels every field the same way above
	 * it (`FilterFieldRow`). Absent = the floating label, as today.
	 */
	hideLabel?: boolean;
	/** Text shown in the empty picker — the field's cue once it has no floating label. Absent = none, as today. */
	placeholder?: string;
};

/**
 * Whether the department picks by JOB rather than by role group — the one rule this selector branches on, exported so
 * a form that reads the selection (is anything chosen? what do I post?) asks the same question the picker answered.
 */
export const usesJobSelection = (jobManagementEnabled: boolean, jobs: readonly Job[]): boolean => jobManagementEnabled && jobs.length > 0;

export const RoleGroupOrJobSelector: FC<Props> = ({
	selectedRoleGroups,
	onSelectedRoleGroupsChanged,
	selectedJobIds,
	onSelectedJobsChanged,
	singleJob,
	fullWidth,
	hideLabel,
	placeholder,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const jobs = useJobs();

	const roleGroupItems = useMemo(
		() =>
			Object.entries(chosenDepartment.role_groups ?? {}).map(([groupName, roles]) => ({
				value: groupName,
				label: `${groupName} (${(roles as string[]).map((r) => nurseRoleDisplayName[r as NurseRole] ?? r).join(", ")})`,
				selectedLabel: groupName,
			})),
		[chosenDepartment.role_groups],
	);

	if (usesJobSelection(jobManagementEnabled, jobs)) {
		return (
			<JobSelector
				variant="dropdown"
				jobs={jobs}
				selectedJobIds={selectedJobIds}
				onSelectedJobsChanged={onSelectedJobsChanged}
				singleSelect={singleJob}
				fullWidth={fullWidth}
				label={hideLabel ? undefined : t("job")}
				placeholder={placeholder}
				formControlSx={{ minWidth: 200, backgroundColor: "white" }}
			/>
		);
	}

	return (
		<SystemDropdown
			multiple
			fullWidth
			clearable
			items={roleGroupItems}
			selectedItems={selectedRoleGroups}
			onChangeMultiple={onSelectedRoleGroupsChanged}
			label={hideLabel ? undefined : t("role.groups")}
			placeholder={placeholder}
			formControlSx={{ minWidth: 200, backgroundColor: "white" }}
		/>
	);
};
